import * as _ from 'lodash';
import React, { Fragment, useRef, useState } from 'react';
import { IAccepted, IDonor, IDonorProfilePreSelectLogData, IDonorsPerMonthByAbo, IEwtSummary, IFwtSummary, IMatchRunPosition, IMatchRunSettings, IMortalityCalculations, IOneWay, IPosition, IRecipient, ISingleEpletMmRisk, ISoleSourceDonor, ISwapFailureAnalysis } from '@nkr/models/index';
import { MatchRunType, MatchStatus } from '@nkr/enums/index';
import { IChainDisplay, IConfirmRexm } from './models';
import { MatchResultsTableWrapper } from "../styles";
import PositionRow from './position-row';
import RecipientOverlay from './recipient-overlay';
import MultipleBridgeDonorsModal from './multiple-bridge-donors-modal';
import EpletMmGraphOverlay from './eplet-mm-graph/eplet-mm-graph-overlay';
import AcceptsOverlay from './accepts-overlay';
import { IDonorProfileAcceptsResponse } from '@nkr/store/index';
import MortalityBreakdown from '@nkr/components/mortality-breakdown-modal';
import FwlDetailsModal from '@nkr/views/target-new/modals/fwt-details-modal';
import EwtDetails from '@nkr/views/target-new/modals/ewt-details';
import { Well } from 'react-bootstrap';

interface IProps {
    list: IChainDisplay[],
    settings: IMatchRunSettings,
    swapFailures: ISwapFailureAnalysis[],
    matchRunType: MatchRunType,
    oneWays: IOneWay[],
    epletMmRiskList: ISingleEpletMmRisk[],
    donorsPerMonthByAbo: IDonorsPerMonthByAbo,
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    onRequestExm: (oneWayId: number) => void,
    onBatchRequestExm: (oneWayList: number[]) => void,
    onMakeMatchOffer: (donorList: { [key: number]: number }, recipientList: { [key: number]: number }) => void,
    onHide: (swapIndex: number, chainIndex: number) => void,
    onGetAcceptsData: (id: number, centerId: number) => Promise<IDonorProfileAcceptsResponse>
}

interface IOverlayState {
    show: boolean,
    donor: IDonor,
    recipient: IRecipient,
    donors: ISoleSourceDonor[],
    intendedDonors: IDonor[],
    dataHtml: string,
    rowRef: any
}

interface IBridgeDonorModal {
    show: boolean,
    swapIndex: number,
    chainIndex: number,
    recipient: IRecipient,
    donors: IDonor[]
}

interface IEpletOverlay {
    show: boolean, rowRef: any,
    recipient: IRecipient
}

interface IAcceptsOverlay {
    show: boolean, rowRef: any, data: IDonorProfilePreSelectLogData
}

const Positions = (props: IProps) => {
    const [overlay, setOverlay] = useState<IOverlayState>({
        show: false,
        donor: null,
        recipient: null,
        intendedDonors: [],
        donors: [],
        dataHtml: '',
        rowRef: null
    });

    const [bridgeDonorModal, setBridgeDonorModal] = useState<IBridgeDonorModal>({
        show: false,
        swapIndex: null,
        chainIndex: null,
        recipient: null,
        donors: [],
    });

    const [epletOverlay, setEpletOverlay] = useState<IEpletOverlay>({
        show: false,
        rowRef: null,
        recipient: null
    });

    const [acceptsData, setAcceptsData] = useState<IDonorProfilePreSelectLogData>(null);

    const [acceptsOverlay, setAcceptsOverlay] = useState<IAcceptsOverlay>({
        show: false,
        rowRef: null,
        data: null
    });

    const tableRef = useRef(null);

    const [mortalityBreakdown, setMortalityBreakdown] = useState<{ show: boolean, recipientAlias: string, data: IMortalityCalculations }>({
        show: false,
        recipientAlias: '',
        data: null
    });

    const [fwt, setFwt] = useState<{
        show: boolean,
        fwtSummary: IFwtSummary
    }>({
        show: false,
        fwtSummary: null
    });

    const [ewt, setEwt] = useState<{
        show: boolean,
        ewtSummary: IEwtSummary,
        initialCount: { [key: string]: number, O: number, A: number, B: number, AB: number }
    }>({
        show: false,
        ewtSummary: null,
        initialCount: null
    });

    const handleHideBridgeDonorChoiceModal = () => {
        setBridgeDonorModal({
            show: false,
            swapIndex: null,
            chainIndex: null,
            recipient: null,
            donors: [],
        })
    }

    const handleShowBridgeDonorChoiceModal = (swapIndex: number, chainIndex: number, recipient: IRecipient, donors: IDonor[]) => {
        if (bridgeDonorModal != null) {
            setBridgeDonorModal({
                show: true,
                swapIndex,
                chainIndex,
                recipient,
                donors
            })
        }
    };

    const handlePreProcessMatchOffer = (swapIndex: number, chainIndex: number) => {
        let chains = props.list.filter(x => x.swapIndex == swapIndex && x.chainIndex == chainIndex);
        let chainItem = chains[0];
        let chainEnd = chainItem.chain.path[chainItem.chain.path.length - 1];
        let recipient = chainEnd.oneWay.recipient;
        let donors = recipient.donors;

        let donorBridges = donors.filter(x => x.statusId <= MatchStatus.Transplanted && x.isExportable);

        if (donorBridges.length > 1) {
            handleShowBridgeDonorChoiceModal(swapIndex, chainIndex, recipient, donorBridges);
        } else {
            handleMakeMatchOffer(swapIndex, chainIndex, donorBridges.length == 1 ? donorBridges[0].id : null)
        }
    };

    const handleUseSelectedBridgeDonor = (swapIndex: number, chainIndex: number, selectedDonorBridgeId: number) => {
        handleMakeMatchOffer(swapIndex, chainIndex, selectedDonorBridgeId)
        handleHideBridgeDonorChoiceModal();
    }

    const handleMakeMatchOffer = (swapIndex: number, chainIndex: number, selectedDonorBridgeId: number = null) => {
        let donorList: { [key: number]: number } = {};
        let recipientList: { [key: number]: number } = {};

        let chains = props.list.filter(x => x.swapIndex == swapIndex && x.chainIndex == chainIndex);

        chains.forEach(chainItem => {
            chainItem.chain.path.forEach((pathItem, k) => {
                donorList[k + 1] = pathItem.donorId;
                recipientList[k + 1] = pathItem.recipientId;

                if (props.matchRunType != MatchRunType.Loops && k == chainItem.chain.path.length - 1) {

                    if (selectedDonorBridgeId != null) {
                        donorList[k + 2] = selectedDonorBridgeId
                    }
                }
            });
        });

        props.onMakeMatchOffer(donorList, recipientList);
    };

    const handleShowOverlay = (donor: IDonor, recipient: IRecipient, soleSourceDonors: ISoleSourceDonor[], ssdHtml: string, rowRef: any, intendedDonors: IDonor[]) => {
        if (overlay != null) {
            setOverlay({
                show: true,
                donor,
                recipient,
                donors: soleSourceDonors,
                intendedDonors,
                dataHtml: ssdHtml,
                rowRef
            });
        }
    };

    const handleHideOverlay = () => {
        let rowRef = overlay.rowRef;

        setOverlay({
            show: false,
            donor: null,
            recipient: null,
            intendedDonors: [],
            donors: [],
            dataHtml: '',
            rowRef
        });
    };

    const handleShowEpletGraph = (recipient: IRecipient, rowRef: any) => {
        setEpletOverlay({
            show: true,
            recipient,
            rowRef
        });
    }

    const handleHideEpletGraph = () => {
        setEpletOverlay(prevData => ({
            ...prevData,
            show: false
        }));
    }

    const handleShowAcceptsData = async (donor: IDonor, rowRef: any) => {
        const id = donor.id;
        const centerId = donor.center?.id;
        await props.showLoading('Loading data, one moment please...');

        try {
            const response = await props.onGetAcceptsData(id, centerId);
            const data = response?.data?.results ?? null;
            setAcceptsData(data);
            setAcceptsOverlay(prev => ({ ...prev, show: true, rowRef, data }));
        } catch (err) {
            console.error('Failed to load accepts data for donor', id, err);
        } finally {
            props.hideLoading();   // always runs now, even on error
        }
    };

    const handleHideAcceptsData = () => {
        setAcceptsOverlay(prevData => ({
            ...prevData,
            show: false,
            data: null,
            rowRef: acceptsOverlay.rowRef
        }));
        setAcceptsData(null);
    }

    const handleShowMortalityBreakdown = (recipient: IRecipient) => {
        setMortalityBreakdown({
            show: true,
            recipientAlias: recipient.alias,
            data: recipient.mortalityCalculations
        })
    }

    const handleHideMortalityBreakdown = () => {
        setMortalityBreakdown({ show: false, recipientAlias: '', data: null });
    }

    const handleShowFwt = (recipient: IRecipient) => {
        let isHardToMatch = recipient?.isHardToMatch;
        let isVoucherHolder = recipient?.isVoucherHolder;
        let isPaired = recipient?.isPairedRecipient;
        let age = Number(recipient?.age ?? '0');

        let fwtSummary: IFwtSummary = {
            recipientId: recipient.id,
            recipientAlias: recipient.alias,
            hardToMatch: isHardToMatch,
            isVoucherHolder,
            isPairedRecipient: isPaired,
            patientCpra: recipient?.cpra4 ?? 0,
            patientHistoricalDeclineRatio: recipient?.declineRatioHistoric ?? 0,
            donorsNeeded: recipient?.donorsNeeded ?? 0,
            aboCombo: recipient?.aboCombo ?? '',
            donorsCompatibleABO: recipient?.donorCompatibleAbo.toString() ?? '',
            aboAndAgeCompatibleDonors: recipient?.aboAndAgeCompatibleDonors.toString() ?? '',
            recipientBloodType: recipient?.bloodType ?? '',
            categories: recipient?.voucherCategories ?? [],
            fwt: recipient?.fWait.toString() ?? '',
            ewt: recipient?.eWait.toString() ?? '',
            awt: recipient?.monthsWaiting.toString() ?? '',
            age: age
        };

        setFwt({
            show: true,
            fwtSummary
        });
    };

    const handleFwtClose = () => {
        setFwt({ ...fwt, show: false });
    };

    const handleShowEwt = (summary: IEwtSummary, initialCount: any) => {
        setEwt({
            show: true,
            ewtSummary: summary,
            initialCount
        });
    };

    const handleEwtClose = () => {
        setEwt({ ...ewt, show: false });
    };

    return (
        <>
            <Well bsSize='small' style={{
                margin: 0,
                padding: '5px 10px',
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                borderBottom: '1px solid #ccc',
                marginBottom: 10
            }}>
                <div style={{ display: 'flex', fontSize: 12 }}>
                    <div
                        title='Pediatric Patient'
                        style={{ display: 'flex', alignItems: 'center', marginRight: 30 }}
                    >
                        <div
                            style={{
                                height: 20,
                                width: 20,
                                backgroundColor: 'yellow',
                                border: '1px solid #ddd',
                                marginRight: 5,
                                borderRadius: 4
                            }}
                        />
                        <div>Pediatric Patient</div>
                    </div>
                    <div
                        title='Sole Source Donor for a patient with a cPRA4 >= 98%'
                        style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}
                    >
                        <div
                            style={{
                                height: 20,
                                width: 20,
                                backgroundColor: '#f57171',
                                border: '1px solid #ddd',
                                marginRight: 5,
                                borderRadius: 4
                            }}
                        />
                        <div>Sole Source Donor for a patient with a cPRA4 &gt;= 98%</div>
                    </div>
                </div>
            </Well>
            <MatchResultsTableWrapper ref={tableRef}>
                <thead>
                    <tr>
                        <th>Pos</th>
                        <th>D Center</th>
                        <th>D Alias</th>
                        <th>R Center</th>
                        <th>R Alias</th>
                        <th>ABO</th>
                        <th>Age</th>
                        <th>Type</th>
                        <th>CPR</th>
                        <th>cPRA4</th>
                        <th>DDR</th>
                        <th>DDRC</th>
                        <th>DDRH</th>
                        <th>EMM</th>
                        <th>HLA</th>
                        <th>A/M</th>
                        <th>HT</th>
                        <th>WT</th>
                        <th>EWT</th>
                        <th>FWT</th>
                        <th style={{ whiteSpace: 'nowrap' }}>MR</th>
                        <th title='Time on dialysis in months'>DT</th>
                        <th>Dist</th>
                        <th>RFP</th>
                    </tr>
                </thead>
                <tbody>
                    {props.list.map((ch, index) =>
                        <Fragment key={'ch-disp-' + index}>
                            {ch.chain.path.map((position, k) =>
                                <PositionRow
                                    key={`ch-disp-swap-${ch.swapIndex}-chain-${ch.chainIndex}-pos-${k}`}
                                    swapIndex={ch.swapIndex}
                                    chainIndex={ch.chainIndex}
                                    position={position}
                                    swapFailures={props.swapFailures}
                                    pathIndex={k}
                                    pathLength={ch.chain.path.length}
                                    matchRunType={props.matchRunType}
                                    confirmRexm={false}
                                    showRexm={true}
                                    oneWays={props.oneWays}
                                    epletMmRiskList={props.epletMmRiskList}
                                    onRequestExm={props.onRequestExm}
                                    onShowOverlay={handleShowOverlay}
                                    onHideOverlay={handleHideOverlay}
                                    onShowEpletMmGraphData={handleShowEpletGraph}
                                    onShowAcceptsData={handleShowAcceptsData}
                                    onShowMortalityBreakdown={handleShowMortalityBreakdown}
                                    onShowEwt={handleShowEwt}
                                    onShowFwt={handleShowFwt}
                                />
                            )}

                            <tr>
                                <td colSpan={22} style={{ paddingBottom: '7px' }}>
                                    <button
                                        type="button"
                                        className="btn btn-danger ghStyle"
                                        onClick={() => handlePreProcessMatchOffer(ch.swapIndex, ch.chainIndex)}
                                    >
                                        Offer {ch.swapIndex}/{ch.chainIndex}
                                    </button>{' '}
                                    <button
                                        type="button"
                                        className="btn btn-warning ghStyle"
                                        onClick={() => props.onHide(ch.swapIndex, ch.chainIndex)}
                                    >
                                        Hide
                                    </button>
                                </td>
                            </tr>
                        </Fragment>
                    )}
                </tbody>
            </MatchResultsTableWrapper>

            <RecipientOverlay
                show={overlay.show}
                rowRef={overlay.rowRef}
                tableRef={tableRef}
                donor={overlay.donor}
                intendedDonors={overlay.intendedDonors}
                recipient={overlay.recipient}
                donors={overlay.donors}
                isMatchingPage={true} />

            <MultipleBridgeDonorsModal
                show={bridgeDonorModal.show}
                swapIndex={bridgeDonorModal.swapIndex}
                chainIndex={bridgeDonorModal.chainIndex}
                recipient={bridgeDonorModal.recipient}
                donors={bridgeDonorModal.donors}
                onCancel={handleHideBridgeDonorChoiceModal}
                onConfirm={handleUseSelectedBridgeDonor} />

            <EpletMmGraphOverlay
                show={epletOverlay.show}
                rowRef={epletOverlay.rowRef}
                tableRef={tableRef}
                recipient={epletOverlay.recipient}
                onHide={handleHideEpletGraph} />

            <AcceptsOverlay
                show={acceptsOverlay.show}
                rowRef={acceptsOverlay.rowRef}
                tableRef={tableRef}
                data={acceptsOverlay.data}
                isAdmin={true}
                onHide={handleHideAcceptsData} />

            <MortalityBreakdown
                show={mortalityBreakdown.show}
                recipientAlias={mortalityBreakdown.recipientAlias}
                data={mortalityBreakdown.data}
                onClose={handleHideMortalityBreakdown}
            />

            <EwtDetails
                show={ewt.show}
                summaryData={ewt.ewtSummary}
                initialCount={ewt.initialCount}
                donorsPerMonthByAbo={props.donorsPerMonthByAbo}
                onClose={handleEwtClose}
            />

            <FwlDetailsModal
                show={fwt.show}
                summaryData={fwt.fwtSummary}
                onClose={handleFwtClose}
            />
        </>
    );
}

export default Positions;